import { el, btn } from '@/ui/dom';
import { buildWaitingBody } from '@/ui/onlineWaiting';
import type { ChampionshipRow, Trophy } from '@/core/Progression';
import { REPORT_FLAG_SVG } from '@/ui/social/playerSheet';
import { ridButton, type RidButton } from '@/ui/social/clubUi';
import { renderTourProgress, type TourStop } from '@/ui/screens/tourProgress';

/**
 * TourIntermissionScreen.ts — between online tour races (M34). Shows the running
 * championship after a race and a READY-up: tap READY and the tour advances to the
 * next city once everyone's ready (or after the server's fallback timer). The
 * globe transition + next race are server-driven, so this screen just gathers
 * readiness and shows where everyone stands.
 */
export interface TourIntermissionCallbacks {
  onReady: () => void;
  onLeave: () => void;
  /** M156 — REPORT A RACER (App Store 1.2). */
  onReport?: () => void;
}

export interface TourIntermissionContext {
  raceNum: number; // the race just finished (1-based)
  tourLen: number;
  cityName: string; // the city just raced
  playerPlaceTrophy: Trophy; // the player's trophy in THIS race (for a little flourish)
  table: ChampionshipRow[]; // the running championship, best first
  /** M158c — the tour standings step: the route + movement since last race. */
  stops: TourStop[];
  prevTable: ChampionshipRow[];
  /** When (Date.now() ms) the server starts the next city (its fallback). */
  nextCityAt: number;
}

export const COUNTDOWN_SECONDS = 20; // matches the server's intermission fallback

export class TourIntermissionScreen {
  private root: HTMLDivElement;
  private panel: HTMLDivElement;
  private status: HTMLDivElement | null = null;
  private readyBtn: RidButton | null = null;
  private countdownEl: HTMLDivElement | null = null;
  private ready = false;
  private countdownTimer: number | null = null;
  private endsAt = 0;
  /** False in matchmade tours: the ready count would reveal the human count. */
  private countsVisible = true;

  constructor(
    parent: HTMLElement,
    private cb: TourIntermissionCallbacks,
  ) {
    // M115 (owner): online rankings share the LEADERBOARD backdrop. This
    // screen only ever appears in an ONLINE tour, so it always wears it.
    this.root = el('div', 'screen screen-intermission online-rank-bg', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'panel results-panel', this.root);
  }

  /** The local player finished, but the server is still waiting on the others
   *  before it sends the official result — a brief honest interstitial so the
   *  finisher isn't staring at a frozen race. Replaced by show() when it lands. */
  showWaiting(raceNum: number, tourLen: number, cityName: string): void {
    this.clearCountdown();
    this.ready = false;
    this.status = null;
    this.readyBtn = null;
    this.panel.replaceChildren();
    this.panel.className = 'panel results-panel results-wait';
    // M115 (owner): shared with the quick-race waiting room so the two can
    // never drift again (they already had — "RACE COMPLETE" vs "FINISHED!"
    // over identical copy).
    buildWaitingBody(
      this.panel,
      `RACE ${raceNum} OF ${tourLen} · ${cityName.toUpperCase()}`,
      'RACE COMPLETE',
      'WAITING FOR THE OTHERS',
    );
    const buttons = el('div', 'results-buttons', this.panel);
    btn('LEAVE TOUR', 'btn btn-danger-ghost', buttons, () => this.cb.onLeave());
    this.root.style.display = '';
  }

  /** M158c (owner): the TOUR STANDINGS step of an online tour — the same
   *  view an offline tour shows (tourProgress.ts) in the dashboard card, with
   *  READY + the server's countdown. Reached from the race's results card. */
  show(ctx: TourIntermissionContext): void {
    this.clearCountdown();
    this.ready = false;
    this.panel.replaceChildren();
    this.panel.className = 'rs-card rs-tour';

    const body = el('div', 'rs-tourbody', this.panel);
    renderTourProgress(body, {
      raceNum: ctx.raceNum,
      tourLen: ctx.tourLen,
      stops: ctx.stops,
      table: ctx.table,
      prevTable: ctx.prevTable,
    });
    if (this.cb.onReport) {
      const onReport = this.cb.onReport;
      const link = el('button', 'mod-report-link results-report', body);
      link.type = 'button';
      el('span', 'mod-link-ic', link).innerHTML = REPORT_FLAG_SVG;
      el('span', '', link).textContent = 'REPORT A RACER';
      link.addEventListener('click', () => onReport());
    }

    const actions = el('div', 'rs-actions', this.panel);
    const info = el('div', 'rs-readyline', actions);
    this.countdownEl = el('div', 'intermission-count', info);
    this.status = el('div', 'lobby-status', info);
    this.readyBtn = ridButton(
      actions,
      { label: 'READY', sub: 'NEXT CITY', variant: 'gold', noChevron: true },
      () => this.onReadyTap(),
    );
    btn('LEAVE TOUR', 'btn btn-danger-ghost rs-quiet', actions, () => this.cb.onLeave());

    this.setStatusText();
    this.startCountdown(ctx.nextCityAt);
    this.root.style.display = '';
  }

  private onReadyTap(): void {
    if (this.ready) return;
    this.ready = true;
    if (this.readyBtn) {
      this.readyBtn.setLabel('READY ✓', 'WAITING FOR THE OTHERS');
      this.readyBtn.el.classList.add('rs-ready-on');
    }
    this.setStatusText();
    this.cb.onReady();
  }

  /** Live "(ready/total)" from the server's lobby messages during the intermission. */
  setReadyCount(ready: number, total: number): void {
    // Matchmade tours hide the count: "(0/1) ready" would reveal how many of the
    // field are actually humans, and the AI backfill is never revealed (M44).
    if (!this.countsVisible) return;
    if (this.status && this.root.style.display !== 'none') {
      this.status.textContent = `${ready} of ${total} ready for the next city`;
    }
  }

  /** Friend tours show the live ready count; matchmade tours suppress it. */
  setCountsVisible(visible: boolean): void {
    this.countsVisible = visible;
  }

  private setStatusText(): void {
    if (!this.status) return;
    this.status.textContent = this.ready
      ? 'Waiting for the others…'
      : 'Tap when you’re ready for the next city';
  }

  /** Counts down to the server's start time (carried over from the race's
   *  results card, so the number keeps running across the two steps). */
  private startCountdown(at = Date.now() + COUNTDOWN_SECONDS * 1000): void {
    this.endsAt = at;
    this.tickCountdown();
    this.countdownTimer = window.setInterval(() => this.tickCountdown(), 500);
  }

  private tickCountdown(): void {
    const left = Math.max(0, Math.ceil((this.endsAt - Date.now()) / 1000));
    if (this.countdownEl) {
      this.countdownEl.textContent = left > 0 ? `NEXT CITY IN ${left}s` : 'NEXT CITY STARTING…';
    }
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  hide(): void {
    this.clearCountdown();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.clearCountdown();
    this.root.remove();
  }
}
